import { FC, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Tag } from '@carbon/react';
import type { ReviewFlag } from './types';
import style from './address-review-flag.module.scss';

interface Props {
  flag: ReviewFlag;
  className?: string;
}

const formatPercent = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '—';
  return `${Math.round(value * 100)}%`;
};

const labelFor = (criticality: ReviewFlag['criticality']): string => {
  return criticality === 'high' ? 'Review: High' : 'Review: Med';
};

const POPOVER_WIDTH = 320;
const POPOVER_GAP = 4;
const VIEWPORT_PADDING = 8;
// Upper bound for the verbose content (header + per-factor rows with
// reasoning text). Used only to decide flip direction.
const ESTIMATED_POPOVER_HEIGHT = 360;

export const AddressReviewFlag: FC<Props> = ({ flag, className }) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(
    null
  );

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const spaceBelow = vh - rect.bottom;
    const placeBelow =
      spaceBelow >= ESTIMATED_POPOVER_HEIGHT + VIEWPORT_PADDING ||
      rect.top < spaceBelow;

    let left = rect.right - POPOVER_WIDTH;
    if (left < VIEWPORT_PADDING) left = VIEWPORT_PADDING;
    if (left + POPOVER_WIDTH > vw - VIEWPORT_PADDING) {
      left = vw - POPOVER_WIDTH - VIEWPORT_PADDING;
    }

    const top = placeBelow
      ? rect.bottom + POPOVER_GAP
      : rect.top - POPOVER_GAP - ESTIMATED_POPOVER_HEIGHT;

    setCoords({ top, left });
  }, [open]);

  return (
    <span
      ref={triggerRef}
      className={`${style.wrapper}${className ? ` ${className}` : ''}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <Tag
        type={flag.criticality === 'high' ? 'red' : 'warm-gray'}
        size="sm"
        className={`${style.chip}${
          flag.criticality === 'medium' ? ` ${style.chipMed}` : ''
        }`}
      >
        {labelFor(flag.criticality)}
      </Tag>

      {open &&
        coords &&
        createPortal(
          <div
            className={style.popover}
            role="tooltip"
            style={{
              top: coords.top,
              left: coords.left,
              width: POPOVER_WIDTH,
            }}
          >
            <div className={style.title}>
              Auto-applied parsing · confidence{' '}
              {formatPercent(flag.confidenceScore)}
            </div>
            {flag.factors.map((factor) => (
              <div key={factor.factor} className={style.factor}>
                <span className={style.factorLabel}>{factor.factor}</span>
                <span className={style.factorScore}>
                  {formatPercent(factor.scored)}
                </span>
                {factor.note && (
                  <span className={style.factorReasoning}>{factor.note}</span>
                )}
              </div>
            ))}
          </div>,
          document.body
        )}
    </span>
  );
};
